import { computed } from 'vue';

import { searchBrandMediaTypes } from '@/app/library/constants';
import {
  baseQueryParamMapping,
  defaultReportingPeriod,
} from '@/components/core/analytics/queryParamMapping';
import { getMetrics, METRICS } from '@/models/metrics.enum';
import { PLATFORM_CONNECTION } from '@/models/platform/platform-connection.enum';
import { usePlatformStore } from '@/stores/platform';

export const useYourPosts = () => {
  const metrics = getMetrics('yourPosts');
  const platformStore = usePlatformStore();

  const sortOptions = computed(() => {
    const includePromoted = !platformStore.isPlatformNeverConnected('facebook_ads');
    const options = [
      { label: 'Recently Published', value: 'DATE' },
      {
        title: 'Organic',
        children: metrics.all([
          METRICS.INSTAGRAM.AVG_VIEW_DURATION,
          METRICS.INSTAGRAM.ORGANIC_COMMENTS,
          METRICS.INSTAGRAM.EFFECTIVENESS_ENGAGEMENTS,
          METRICS.INSTAGRAM.ENGAGEMENT_RATE,
          METRICS.INSTAGRAM.ORGANIC_ENGAGEMENT_RATE_VIEWS,
          METRICS.INSTAGRAM.TOTAL_ENGAGEMENTS,
          METRICS.INSTAGRAM.ENTERTAINMENT_SCORE,
          METRICS.INSTAGRAM.FOLLOWS,
          METRICS.INSTAGRAM.ORGANIC_LIKES,
          METRICS.INSTAGRAM.PROFILE_VISITS,
          METRICS.INSTAGRAM.REACH,
          METRICS.INSTAGRAM.SAVES,
          METRICS.INSTAGRAM.SHARES,
          METRICS.INSTAGRAM.TOTAL_WATCH_TIME,
          METRICS.INSTAGRAM.VIEWS,
        ]),
      },
    ];
    if (includePromoted) {
      options.push(
        {
          title: 'Organic + Promoted',
          children: metrics.all([
            METRICS.INSTAGRAM.TOTAL_COMMENTS,
            METRICS.INSTAGRAM.SUM_TOTAL_ENGAGEMENTS,
            METRICS.INSTAGRAM.TOTAL_LIKES,
            METRICS.INSTAGRAM.PAID_AND_ORGANIC_REACH,
            METRICS.INSTAGRAM.PAID_AND_ORGANIC_SAVED,
            METRICS.INSTAGRAM.PAID_AND_ORGANIC_SHARES,
          ]),
        },
        {
          title: 'Promoted',
          children: metrics.all([
            METRICS.INSTAGRAM.AMOUNT_SPENT,
            METRICS.INSTAGRAM.PAID_COMMENTS,
            METRICS.INSTAGRAM.CPC,
            METRICS.INSTAGRAM.CPM,
            METRICS.INSTAGRAM.CTR,
            METRICS.INSTAGRAM.PAID_ENGAGEMENT_RATE,
            METRICS.INSTAGRAM.PAID_TOTAL_ENGAGEMENTS,
            METRICS.INSTAGRAM.PAID_IMPRESSIONS,
            METRICS.INSTAGRAM.PAID_LIKES,
            METRICS.INSTAGRAM.PAID_REACH,
            METRICS.INSTAGRAM.PAID_SAVES,
            METRICS.INSTAGRAM.PAID_SHARES,
            METRICS.INSTAGRAM.PAID_VIDEO_VIEWS,
            METRICS.INSTAGRAM.THRUPLAYS,
            METRICS.INSTAGRAM.VIDEO_PLAYS_25,
            METRICS.INSTAGRAM.VIDEO_PLAYS_50,
            METRICS.INSTAGRAM.VIDEO_PLAYS_75,
            METRICS.INSTAGRAM.VIDEO_PLAYS_100,
          ]),
        },
      );
    }
    options.push({
      title: 'Discontinued',
      children: metrics.all([
        METRICS.INSTAGRAM.TOTAL_PLAYS,
        METRICS.INSTAGRAM.EFFECTIVENESS,
        METRICS.INSTAGRAM.ENGAGEMENT_RATE_IMPRESSIONS,
        METRICS.INSTAGRAM.ORGANIC_IMPRESSIONS,
        METRICS.INSTAGRAM.TOTAL_IMPRESSIONS,
        METRICS.INSTAGRAM.VIDEO_VIEWS,
        METRICS.INSTAGRAM.TOTAL_VIDEO_VIEWS,
      ]),
    });
    return options;
  });

  const banners = computed(() => {
    const bannerConfigs = [];
    if (platformStore.isPlatformNeverConnected('facebook_ads')) {
      bannerConfigs.push({
        severity: 'info',
        message:
          'Connect your Meta Ads account for promoted post and ad insights, plus ad comments in Community',
        callToAction: 'Connect',
        onAction: () => platformStore.connect('facebook_ads'),
      });
    }
    if (
      !platformStore.isPlatformNeverConnected('facebook_ads') &&
      !platformStore.isPlatformConnected('facebook_ads')
    ) {
      bannerConfigs.push({
        severity: 'warn',
        message: 'Meta Ads not connected, data may not be current',
        callToAction: 'Reconnect',
        onAction: () => platformStore.connect('facebook_ads'),
      });
    }
    return bannerConfigs;
  });

  return {
    title: 'Instagram Posts',
    channelFilterKey: 'instagram',
    filters: {
      brandMediaTypes: [
        searchBrandMediaTypes.INSTAGRAM_OWNED,
        searchBrandMediaTypes.INSTAGRAM_OWNED_IGTV,
      ],
      default: {
        captionContain: [],
        captionNotContain: [],
        contentTags: [],
        contentTagMatchType: 'ANY',
        postType: [],
        isBoosted: undefined,
        reportingPeriod: defaultReportingPeriod,
        sort: {
          sortOrder: 'DESC',
          selected: { value: 'DATE', label: 'Recently Published' },
        },
      },
      options: {
        isBoosted: [
          { label: 'Organic Posts', value: false },
          { label: 'Promoted Posts', value: true },
        ],
        postType: [
          { label: 'Photo', value: 'image' },
          { label: 'Video', value: 'video' },
          { label: 'Reel', value: 'reel' },
          { label: 'Carousel', value: 'carousel' },
        ],
      },
      queryParamMapping: {
        ...baseQueryParamMapping,
        isBoosted: { queryParamName: 'isBoosted', type: 'boolean' },
        postType: { queryParamName: 'postType', type: 'array', elementType: 'string' },
        captionContain: {
          queryParamName: 'captionContain',
          type: 'array',
          elementType: 'string',
        },
        captionNotContain: {
          queryParamName: 'captionNotContain',
          type: 'array',
          elementType: 'string',
        },
      },
      scope: 'instagramYourPosts',
      sortDetails: {
        options: sortOptions,
        default: {
          sortOrder: 'DESC',
          selected: { value: 'DATE', label: 'Recently Published' },
        },
      },
    },
    displayMetrics: metrics.all([
      METRICS.INSTAGRAM.ENGAGEMENT_RATE,
      METRICS.INSTAGRAM.EFFECTIVENESS_ENGAGEMENTS,
      METRICS.INSTAGRAM.TOTAL_ENGAGEMENTS,
      METRICS.INSTAGRAM.REACH,
    ]),
    metricLabels: metrics.all(Object.values(METRICS.INSTAGRAM)),
    download: {
      pdf: {
        fileName: 'yourPostsPDF',
        type: 'YourPosts',
        label: 'Your Posts PDF',
        pendingKey: 'YourPostsReportPending',
      },
      csv: {
        fileName: 'yourPostsCSV',
        type: 'instagramPosts',
        label: 'Your Posts CSV',
        pendingKey: 'instagramPostsCSVPending',
      },
    },
    platformConnection: PLATFORM_CONNECTION.FACEBOOK,
    banners,
  };
};
